/**
 * The bar under the job title on the new job page (job page v2, slice 2):
 *
 *   [⇄ Move to ▾]  Office pins  [Job details] [City filing] … [⋯ More for this job ⌘K]
 *
 * - **Move to ▾** is the ONE status button, and it is quiet: the Finish
 *   Line's Next stays the only filled button on the page. It lists every move
 *   from the job-actions list, greying the ones that don't apply with the
 *   reason ("● now" for where the job is). Hold asks why first.
 * - **Pins** are the person's own (or the office default) and run the same
 *   action ⌘K and the menus run. A pin that doesn't apply to this job stays
 *   in its place, greyed, with the reason on hover.
 * - **More for this job** opens ⌘K, whose "This job" group is the full list.
 */
import { useEffect, useState } from "react";
import { ArrowLeftRight, ChevronDown, MoreHorizontal } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { JobAction, JobActionId } from "./jobActions";

/** The Move to ▾ list, in this order. */
export const MOVE_MENU_IDS: JobActionId[] = [
    "approve",
    "move:in_progress",
    "move:scheduled",
    "move:awaiting_scheduling",
    "move:en_route",
    "move:arrived",
    "move:office_review",
    "move:cannot_complete",
    "move:held",
    "send-back",
    "reopen",
    "cancel",
];

export const HOLD_REASONS = [
    "Waiting on the customer",
    "Waiting on parts",
    "Waiting on the city",
    "Something else",
] as const;

const PIN =
    "inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[13.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-card";

/** Job page v3 (Job at a glance): the same pins on the header's dark band —
 *  icon plus word, the icon in brass, never an icon alone. */
const BAND_PIN =
    "inline-flex min-h-9 items-center gap-2 rounded-lg border border-[#3B3A36] bg-[#2B2A27] px-3 py-1.5 text-[14px] font-bold text-[#F3F0E9] hover:border-[#C9A24B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A24B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#22211F] disabled:cursor-not-allowed disabled:text-[#A8A398] disabled:hover:border-[#3B3A36] [&>svg]:text-[#C9A24B]";

export function JobActionBar({
    actions,
    pins,
    pinsAreOfficeDefault,
    onResetPins,
    onHold,
    holdRequest,
    onMoreForThisJob,
    tone = "page",
}: {
    /** "band": drawn on the Job at a glance header (job page v3). */
    tone?: "page" | "band";
    actions: JobAction[];
    pins: JobActionId[];
    pinsAreOfficeDefault: boolean;
    onResetPins: () => void;
    /** Put the job on hold with the chosen reason. */
    onHold: (reason: string) => void;
    /** Bump to open Move to ▾ at "Hold · why?" (Hold from ⌘K). */
    holdRequest?: number;
    onMoreForThisJob: () => void;
}) {
    const byId = new Map(actions.map((a) => [a.id, a]));
    const band = tone === "band";
    const pin = band ? BAND_PIN : PIN;
    const [step, setStep] = useState<"list" | "hold">("list");
    const [why, setWhy] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!holdRequest) return;
        setStep("hold");
        setOpen(true);
    }, [holdRequest]);

    return (
        // One row that scrolls sideways on a phone (the header is sticky, so a
        // wrapped bar would eat half the screen); it wraps from sm up.
        <div
            className={cn(
                band
                    ? "flex flex-wrap items-center gap-2"
                    : "-mx-1 mt-2 flex flex-nowrap items-center gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0 [&>*]:shrink-0",
            )}
            data-testid="job-action-bar"
        >
            <DropdownMenu
                open={open}
                onOpenChange={(o) => {
                    setOpen(o);
                    if (!o) {
                        setStep("list");
                        setWhy(null);
                    }
                }}
            >
                <DropdownMenuTrigger asChild>
                    <button type="button" className={pin} data-testid="move-to" data-teach="job.move-to">
                        <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
                        Move to
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80">
                    {step === "list" ? (
                        <>
                            <DropdownMenuLabel className="text-[12px] text-muted-foreground">
                                Move this job to
                            </DropdownMenuLabel>
                            {MOVE_MENU_IDS.map((id) => byId.get(id))
                                .filter((a): a is JobAction => !!a)
                                .map((a) => {
                                    const Icon = a.icon;
                                    return (
                                        <DropdownMenuItem
                                            key={a.id}
                                            disabled={!a.applies}
                                            data-testid={`move-to-${a.id}`}
                                            className={cn(a.destructive && "text-red-700 dark:text-red-400")}
                                            onSelect={(e) => {
                                                if (a.id === "move:held") {
                                                    e.preventDefault();
                                                    setStep("hold");
                                                    return;
                                                }
                                                a.run();
                                            }}
                                        >
                                            <Icon className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden />
                                            <span className="min-w-0 flex-1">{a.label}</span>
                                            {!a.applies && a.reason && (
                                                <span className="ml-3 text-[12px] text-muted-foreground">
                                                    {a.reason === "now" ? "● now" : a.reason}
                                                </span>
                                            )}
                                        </DropdownMenuItem>
                                    );
                                })}
                        </>
                    ) : (
                        <div data-testid="hold-why">
                            <DropdownMenuLabel>Hold · why?</DropdownMenuLabel>
                            {HOLD_REASONS.map((r) => (
                                <DropdownMenuItem
                                    key={r}
                                    role="menuitemradio"
                                    aria-checked={why === r}
                                    className={cn(why === r && "bg-muted font-semibold")}
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setWhy(r);
                                    }}
                                >
                                    <span className="mr-2 w-3.5 text-center" aria-hidden>
                                        {why === r ? "●" : "○"}
                                    </span>
                                    {r}
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                disabled={!why}
                                data-testid="hold-go"
                                className="font-semibold"
                                onSelect={() => why && onHold(why)}
                            >
                                Put on hold
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setStep("list");
                                    setWhy(null);
                                }}
                            >
                                ← Back
                            </DropdownMenuItem>
                        </div>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <span
                className={cn("ml-1 text-[12.5px]", band ? "text-[#A8A398]" : "text-muted-foreground")}
                data-testid="pins-label"
                data-teach="job.pins"
            >
                {pinsAreOfficeDefault ? "Office pins" : "Your pins"}
            </span>
            {pins
                .map((id) => byId.get(id))
                .filter((a): a is JobAction => !!a)
                .map((a) => {
                    const Icon = a.icon;
                    return (
                        <button
                            key={a.id}
                            type="button"
                            className={pin}
                            disabled={!a.applies}
                            title={a.applies ? a.label : `${a.label}: ${a.reason}`}
                            onClick={() => a.run()}
                            data-testid={`pin-${a.id}`}
                        >
                            <Icon className="h-3.5 w-3.5" aria-hidden />
                            {a.pinLabel}
                        </button>
                    );
                })}
            <button
                type="button"
                className={pin}
                onClick={onMoreForThisJob}
                data-testid="more-for-this-job"
                data-teach="job.more"
            >
                <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                More for this job
                <kbd
                    className={cn(
                        "ml-1 rounded border px-1 text-[11px] font-normal",
                        band ? "border-[#3B3A36] text-[#A8A398]" : "border-border text-muted-foreground",
                    )}
                >
                    ⌘K
                </kbd>
            </button>
            {!pinsAreOfficeDefault && (
                <button
                    type="button"
                    className={cn(
                        "text-[13px] font-medium underline-offset-2 hover:underline",
                        band ? "text-[#A8A398] hover:text-[#F3F0E9]" : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={onResetPins}
                    data-testid="reset-pins"
                >
                    Reset to office default
                </button>
            )}
        </div>
    );
}
